// src/components/ContactSection.js
//
// Record 610 (ruling 259): the Contact section of a person's profile - his mailing address, phone and e-mail, kept
// on the platform and edited by him. Ruling 259: "There shouldn't be any reason to not have anyone's personal contact
// information listed in the platform ... if there isnt one listed, then they must be afforded the opportunity to
// enter it, handle it with grace."
//
// It writes the keys the profile already holds (street, city, state, zip, phone - src\data\contactDetails.js
// CONTACT_KEYS), through profilePatch() (PRF-1), to his OWN users/{uid}: firestore.rules memberWritableOnly() admits
// exactly these. The e-mail is shown, not edited - contactDetails.js says why (it is how the platform knows a filer is
// the claimant). A claim keeps its own copy of the address it was filed with; changing the profile changes the next
// claim and every letter from now on, not a claim already on file.
//
// Used twice: on the Dashboard (every role opens it), and inside the sign-in reminder (AddressReminder.js).
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { profilePatch } from '../data/memberProfile';
import { contactPatchFor, hasMailingAddress, mailingAddressLines, CONTACT_KEYS } from '../data/contactDetails';

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', ok: '#5FB878', text: '#DCE7F2', bad: '#E06C6C', panel: '#0B1626' };
const s = (v) => (v == null ? '' : String(v).trim());
const LABEL = { name: 'Your name as it prints on claims and letters', street: 'Street', city: 'City', state: 'State', zip: 'ZIP', phone: 'Phone' };

export default function ContactSection({ user, startOpen, onSaved, embedded }) {
  const [profile, setProfile] = useState(undefined);   // undefined: not read yet; null: no profile document
  const [open, setOpen] = useState(!!startOpen);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // Forensic audit 2026-10-16: a failed read used to leave profile undefined - "Reading your profile…" forever, with
  // "Add my address" live over a blank form. The failure is now said, and nothing is saved over a profile nobody read.
  const [readErr, setReadErr] = useState('');
  const uid = user && user.uid;
  useEffect(() => {
    let dead = false;
    if (!uid) return undefined;
    setReadErr('');
    getDoc(doc(db, 'users', uid)).then((d) => { if (!dead) setProfile(d.exists() ? d.data() : null); })
      .catch((e) => { if (!dead) { setProfile(undefined); setReadErr('Your profile could not be read (' + ((e && (e.code || e.message)) || 'refused') + '). This is NOT "nothing on file" - reload the page to see or change your contact details.'); } });
    return () => { dead = true; };
  }, [uid]);
  useEffect(() => {
    const p = profile || {};
    const f = {}; CONTACT_KEYS.forEach((k) => { f[k] = s(p[k]); });
    setForm(f);
  }, [profile]);
  if (!uid) return null;

  const save = async () => {
    if (readErr || profile === undefined) { setMsg('Not saved: your profile has not been read, so nothing is written over it.'); return; }   // forensic audit 2026-10-16
    const r = contactPatchFor(form);
    if (!r.ok) { setMsg(r.errors.join(' ')); return; }
    setBusy(true); setMsg('');
    try {
      await setDoc(doc(db, 'users', uid), profilePatch(r.patch), { merge: true });
      setProfile((p) => ({ ...(p || {}), ...r.patch }));
      setMsg('Saved to your profile.');
      setOpen(false);
      if (onSaved) onSaved(r.patch);
    } catch (e) {
      setMsg('Not saved: ' + ((e && (e.code || e.message)) || 'the write was refused') + '. Nothing was changed.');
    }
    setBusy(false);
  };

  const p = profile || {};
  const have = hasMailingAddress(p);
  const box = embedded ? { marginTop: 8 } : { border: '1px solid ' + (have || profile === undefined ? C.line : C.accent), background: C.panel, padding: '10px 14px', marginBottom: 14 };
  return (
    <div style={box}>
      {!embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Contact</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>Your mailing address, phone and e-mail. The address prints on the claims and letters you sign, and is where the Carrier sends its replies.</div>
          </div>
          {!open && profile !== undefined && <button className="btn btn-secondary btn-sm" onClick={() => { setOpen(true); setMsg(''); }}>{have ? 'Change' : 'Add my address'}</button>}
        </div>
      )}
      {readErr ? (
        <div style={{ fontSize: 12, color: C.bad, marginTop: 6 }}>&#9888; {readErr}</div>
      ) : !open ? (
        <div style={{ fontSize: 12, color: C.text, marginTop: 6 }}>
          {profile === undefined ? <span style={{ color: C.dim }}>Reading your profile…</span> : (
            <>
              <div>Name: {s(p.name) || s(user.name) || 'not on file'}</div>
              <div>{have ? mailingAddressLines(p).join(', ') : <span style={{ color: C.accent }}>No mailing address on file yet.</span>}</div>
              <div style={{ color: C.dim }}>Phone: {s(p.phone) || 'not on file'} · E-mail: {s(user.email) || 'not on file'}</div>
            </>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
            {CONTACT_KEYS.map((k) => (
              <label key={k} style={{ fontSize: 11, color: C.dim, gridColumn: k === 'street' || k === 'name' ? '1 / -1' : undefined }}>
                {LABEL[k]}
                <input aria-label={k} className="form-input" style={{ fontSize: 12, marginTop: 2 }} value={form[k] || ''} inputMode={k === 'zip' || k === 'phone' ? 'tel' : undefined}
                  onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); }} />
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>E-mail: {s(user.email) || 'not on file'} - the address you sign in with. It is how the platform knows a claim is your own, so it is changed by the administrator, not here.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => { setOpen(false); setMsg(''); }}>Not now</button>
          </div>
        </div>
      )}
      {msg ? <div style={{ fontSize: 11, color: /^Not saved/.test(msg) ? C.bad : (/^Saved/.test(msg) ? C.ok : C.accent), marginTop: 6 }}>{msg}</div> : null}
      {!embedded && <div style={{ fontSize: 10, color: C.dim, marginTop: 6 }}>Your profile is read by you and the platform administrator. A claim keeps its own copy of the address it was filed with.</div>}
    </div>
  );
}
